import React, { FC } from 'react';
import { CampaignsCreditPackageCard } from './CampaignsCreditPackageCard';

type CreditPackage = {
  id: string;
  title: string;
  messages: string;
  price: string;
  features: string[];
  ctaLabel: string;
  isPopular?: boolean;
};

const BALANCE_DATA = {
  title: 'Pay-as-you-grow',
  subtitle: 'WhatsApp messaging credit',
  description: 'No monthly fee, no surprises. One credit pool for marketing, utility and authentication templates. Billed only when a conversation actually delivers.',
  availableBalanceLabel: 'Credit Balance',
  availableBalanceValue: '45,000',
  availableBalanceUnit: 'credits available',
  renewalHint: 'Auto top-up enabled',
};

const CREDIT_PACKAGES: CreditPackage[] = [
  {
    id: 'starter',
    title: 'Starter',
    messages: '5k',
    price: '€50',
    features: ['1,000 conversations', 'No setup cost', 'Standard delivery rate'],
    ctaLabel: 'Buy starter',
  },
  {
    id: 'growth',
    title: 'Growth',
    messages: '25k',
    price: '€225',
    features: ['5,000 conversations', 'Volume discount 10%', 'Priority support'],
    ctaLabel: 'Buy growth',
    isPopular: true,
  },
  {
    id: 'business',
    title: 'Business',
    messages: '100k',
    price: '€800',
    features: ['20,000 conversations', '24/7 priority support', 'API billing unit'],
    ctaLabel: 'Buy business',
  },
  {
    id: 'enterprise',
    title: 'Enterprise',
    messages: '500k',
    price: '€3500',
    features: ['100,000 conversations', 'Custom SLA and DPA', 'API plus webhook limits'],
    ctaLabel: 'Buy enterprise',
  },
];

export const CampaignsCreditPackagesSection: FC = () => {
  return (
    <section className='space-y-6 rounded-3xl border border-secondary-border bg-surface p-5 shadow-sm md:p-6'>
      <div className='space-y-1'>
        <p className='text-xs font-semibold uppercase tracking-[0.16em] text-quaternary-500'>WhatsApp billing</p>
        <h2 className='text-3xl font-semibold text-slate-900'>Top up your WhatsApp credit</h2>
        <p className='text-sm text-secondary-600'>Buy message bundles in advance and get better rates. Credit never expires and works across all your campaigns.</p>
      </div>

      <div className='rounded-2xl bg-gradient-to-r from-blue-800 to-blue-600 p-5 text-white-100 md:p-6'>
        <div className='flex flex-col gap-5 md:flex-row md:items-end md:justify-between'>
          <div className='max-w-2xl space-y-2'>
            <p className='text-xs font-semibold uppercase tracking-[0.16em] text-blue-100'>{BALANCE_DATA.title}</p>
            <h3 className='text-2xl font-semibold'>{BALANCE_DATA.subtitle}</h3>
            <p className='text-sm leading-6 text-blue-100'>{BALANCE_DATA.description}</p>
          </div>

          <div className='w-full rounded-xl border border-blue-300/40 bg-blue-900/30 p-4 md:w-auto md:min-w-[220px]'>
            <p className='text-[11px] uppercase tracking-[0.16em] text-blue-100'>{BALANCE_DATA.availableBalanceLabel}</p>
            <p className='mt-2 text-3xl font-semibold'>{BALANCE_DATA.availableBalanceValue}</p>
            <p className='text-xs text-blue-100'>{BALANCE_DATA.availableBalanceUnit}</p>
            <p className='mt-3 text-xs text-blue-100'>{BALANCE_DATA.renewalHint}</p>
          </div>
        </div>
      </div>

      <div className='space-y-4'>
        <div className='text-center'>
          <p className='text-xs font-semibold uppercase tracking-[0.16em] text-quaternary-500'>Choose your pack</p>
          <h3 className='text-xl font-semibold text-slate-900'>The more you buy, the less you pay</h3>
        </div>

        <div className='grid gap-4 md:grid-cols-2 xl:grid-cols-4'>
          {CREDIT_PACKAGES.map((item) => (
            <CampaignsCreditPackageCard
              key={item.id}
              title={item.title}
              messages={item.messages}
              price={item.price}
              features={item.features}
              ctaLabel={item.ctaLabel}
              isPopular={item.isPopular}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
